import type {ReactElement} from 'react';
import {useState} from 'react';

/**
 * A custom React hook that manages the state and navigation logic for a modal stepper component.
 * This hook provides functionality to navigate between different steps in a modal, tracking the current step position and navigation direction.
 * It ensures that navigation stays within the bounds of the provided steps array and provides handlers for moving forward and backward through the steps.
 *
 * @param steps An array of React elements representing the different steps in the modal stepper.
 * @returns An object containing the current step index, navigation direction, and handler functions for step navigation.
 *
 * @example
 * ```tsx
 * const steps = [
 *   <Step1Component key="step1" />,
 *   <Step2Component key="step2" />,
 *   <Step3Component key="step3" />
 * ];
 *
 * const { step, direction, handleNext, handleBack } = useModalStepper(steps);
 *
 * return (
 *   <Modal>
 *     {steps[step]}
 *     <button onClick={handleBack} disabled={step === 0}>
 *       Back
 *     </button>
 *     <button onClick={handleNext} disabled={step === steps.length - 1}>
 *       Next
 *     </button>
 *   </Modal>
 * );
 * ```
 */
export const useModalStepper = (steps: ReactElement[]) => {
    const [direction, setDirection] = useState<'backwards' | 'forwards'>('forwards');
    const [step, setStep] = useState(0);
    const maxSteps = steps.length - 1;

    /**
     * Advances to the next step in the modal stepper if not already at the last step.
     * This function updates the direction state to 'forwards' and increments the current step index.
     * The navigation is bounded to prevent going beyond the available steps in the array.
     */
    const handleNext = () => {
        if (step < maxSteps) {
            setDirection('forwards');
            setStep(step + 1);
        }
    };

    /**
     * Navigates to the previous step in the modal stepper if not already at the first step.
     * This function updates the direction state to 'backwards' and decrements the current step index.
     * The navigation is bounded to prevent going below the first step (index 0).
     */
    const handleBack = () => {
        if (step > 0) {
            setDirection('backwards');
            setStep(step - 1);
        }
    };

    return {
        direction,
        handleBack,
        handleNext,
        step
    };
};